import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api.js';
import { fetchQueue } from '../desk-model.js';

function relTime(dateVal) {
  if (!dateVal) return 'NEVER';
  const d = new Date(dateVal);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return 'JUST NOW';
  if (diff < 3600) return `${Math.floor(diff / 60)}M AGO`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}H AGO`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}D AGO`;
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase();
}

export default function Picker() {
  const nav = useNavigate();
  const [projects, setProjects] = useState(null);
  // map of projectId -> { openCalls, pct, stageLabel } | null (null = failed fetch)
  const [signposts, setSignposts] = useState({});
  const [menu, setMenu] = useState(null);    // { project, x, y } | null
  const [dialog, setDialog] = useState(null); // { project } | null
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    api('/api/projects').then(ps => {
      if (!mountedRef.current) return;
      setProjects(ps);

      // Fetch per-project signpost data in parallel; don't block tile render.
      Promise.all(ps.map(async p => {
        try {
          const queue = await fetchQueue(p._id);
          const openCalls = (queue && queue.open ? queue.open.length : 0);
          return [p._id, { openCalls }];
        } catch {
          return [p._id, null];
        }
      })).then(entries => {
        if (!mountedRef.current) return;
        setSignposts(Object.fromEntries(entries));
      });
    });
  }, []);

  useEffect(() => {
    if (!menu) return;
    function dismiss() { setMenu(null); }
    function keyDismiss(e) { if (e.key === 'Escape') dismiss(); }
    window.addEventListener('click', dismiss);
    window.addEventListener('scroll', dismiss, true);
    window.addEventListener('keydown', keyDismiss);
    return () => {
      window.removeEventListener('click', dismiss);
      window.removeEventListener('scroll', dismiss, true);
      window.removeEventListener('keydown', keyDismiss);
    };
  }, [menu]);

  function onCardContextMenu(e, project) {
    e.preventDefault();
    setMenu({ project, x: e.clientX, y: e.clientY });
  }

  async function performArchive(project) {
    await api(`/api/projects/${project._id}`, { method: 'PATCH', body: { archived: true } });
    setProjects(prev => prev.filter(p => p._id !== project._id));
    setDialog(null);
  }

  if (projects === null) return null;

  const subtitle = 'Entertainment Project Incubator';

  return (
    <div className="picker">
      <header className="pk-hero">
        <h1>The workshop<span className="dot">.</span></h1>
        <p>{subtitle}</p>
      </header>

      {/* New project affordance: a button, NOT a seed tile */}
      <button
        type="button"
        className="pk-seed"
        onClick={() => window.dispatchEvent(new CustomEvent('openInit', { detail: 'new' }))}
      >
        <span className="plus">+</span>
        <span className="pk-seed-line">Plant a new seed.</span>
        <span className="pk-seed-sub">A NEW SILO · ITS OWN AGENTS · ITS OWN MEMORY</span>
      </button>

      {projects.map(p => {
        const sp = signposts[p._id]; // { openCalls } | null | undefined
        const openCalls = sp ? sp.openCalls : null;
        const signpostsLoaded = sp !== undefined; // undefined = still loading
        return (
          <button
            key={p._id}
            type="button"
            className="pk-proj"
            style={{ '--m': p.colorMark }}
            onClick={() => nav(`/project/${p._id}`)}
            onContextMenu={(e) => onCardContextMenu(e, p)}
          >
            <div className="pk-proj-main">
              <div className="pk-proj-title">{p.title}</div>
              <div className="pk-proj-meta mono-label">
                {(p.format || '').toUpperCase()}{p.format && p.phase ? ' · ' : ''}{(p.phase || '').toUpperCase()}
                {p.opened ? ` · OPENED ${new Date(p.opened).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }).toUpperCase()}` : ''}
              </div>
              {p.logline && <div className="pk-proj-logline">"{p.logline}"</div>}
            </div>

            <div className="pk-proj-stats">
              {/* Signpost 1: Calls waiting */}
              <div className="pk-stat">
                {signpostsLoaded && sp ? (
                  <>
                    <span className={`pk-stat-n${openCalls > 0 ? ' hot' : ''}`}>{openCalls}</span>
                    <span className="mono-label">{openCalls === 1 ? 'CALL WAITING' : 'CALLS WAITING'}</span>
                  </>
                ) : (
                  <>
                    <span className="pk-stat-n dim">{signpostsLoaded ? '—' : '…'}</span>
                    <span className="mono-label">CALLS WAITING</span>
                  </>
                )}
              </div>

              {/* Signpost 2: Since touched */}
              <div className="pk-stat">
                <span className="pk-stat-n dim">{relTime(p.lastTouched)}</span>
                <span className="mono-label">SINCE TOUCHED</span>
              </div>
            </div>

            <span className="pk-enter mono-label">ENTER →</span>
          </button>
        );
      })}

      <footer className="pk-foot mono-label">
        SILOED · NOTHING CROSSES PROJECTS · AGENTS INSTANCED PER PROJECT, NO SHARED MEMORY
      </footer>

      {menu && (
        <div
          className="ctx-menu"
          style={{
            position: 'fixed', left: menu.x, top: menu.y, zIndex: 100,
            background: 'var(--panel-2)', border: '1px solid var(--rule-2)',
            padding: 4, minWidth: 180, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            style={{
              display: 'block', width: '100%', padding: '8px 12px',
              background: 'transparent', border: 0, cursor: 'pointer',
              fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em',
              textTransform: 'uppercase', color: 'var(--accent-ink)', textAlign: 'left',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--panel-3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            onClick={() => { setDialog({ project: menu.project }); setMenu(null); }}
          >Delete project</button>
        </div>
      )}

      {dialog && (
        <ConfirmDeleteDialog
          project={dialog.project}
          onCancel={() => setDialog(null)}
          onConfirm={() => performArchive(dialog.project)}
        />
      )}
    </div>
  );
}

function ConfirmDeleteDialog({ project, onCancel, onConfirm }) {
  const [armed, setArmed] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  async function handleClick() {
    if (!armed) {
      setArmed(true);
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setArmed(false), 3000);
      return;
    }
    clearTimeout(timeoutRef.current);
    setBusy(true);
    try {
      await onConfirm();
    } catch (e) {
      setErr(e.body?.error || e.message || 'Delete failed');
      setBusy(false);
      setArmed(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <h2 style={{ fontFamily: 'var(--font-sans)', fontWeight: 500, margin: '0 0 8px' }}>
          Delete <em>{project.title}</em>?
        </h2>
        <p style={{ fontFamily: 'var(--font-sans)', fontStyle: 'italic', color: 'var(--ink-2)', margin: '0 0 24px', lineHeight: 1.5 }}>
          This will hide it from the workshop. The data is preserved and can be restored from Mongo if needed. This action cannot be undone from the UI.
        </p>
        {err && <p style={{ color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 12, margin: '0 0 16px' }}>{err}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            type="button"
            className="btn accent"
            onClick={handleClick}
            disabled={busy}
            style={{ minWidth: 220 }}
          >
            {busy ? 'Deleting...' : armed ? 'Really delete? Click again to confirm' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}
